import { aAccesFinances, canAccessPage } from "@/lib/role-permissions";
import { useClotureSemaine } from "@/hooks/useFinance";
import { useIntentionsDeMesseCompletes, useIntentionsARelire } from "@/hooks/useIntentions";
import { useSacrementRequestsByParish } from "@/hooks/useSacrementRequests";
import { useVisitesPastorales } from "@/hooks/useVisitesPastorales";
import { useCompteReversement } from "@/hooks/useTableauDeBord";
import { semaine } from "@/pages/dashboard-parts/periodes";
import { calculerAlertes, type Alerte } from "@/pages/dashboard-parts/alertes";

/**
 * Profils qui lisent les « Alertes de la paroisse » : le curé (manuel 2,
 * Action 07) et le conseiller, en lecture (Action 41). Les autres profils ont
 * « Ce qui vous attend », de leurs seules sources.
 */
export const ROLES_ALERTES_PAROISSE = ["cure", "conseiller"];

export function voitAlertesParoisse(role: string | null | undefined): boolean {
  return !!role && ROLES_ALERTES_PAROISSE.includes(role);
}

export interface AlertesParoisse {
  alertes: Alerte[];
  /** Alertes rouges (« décision dans la journée »). */
  duJour: number;
  enCours: boolean;
  enPanne: boolean;
  /** Toutes les sources (curé) ; sinon lecture partielle (conseiller). */
  complet: boolean;
}

/**
 * Alertes de la paroisse, classées par urgence : une seule source pour le
 * tableau de bord, le centre de notifications et le point rouge du menu
 * (manuel 2, Action 03 : « le point rouge sur Notifications signale des
 * alertes non lues », « classées par urgence », « touchez une alerte pour
 * aller à l'écran concerné »).
 *
 * Mêmes lectures, mêmes clés React Query que le tableau de bord : le cache est
 * partagé, rien n'est demandé deux fois. `actif=false` (ou un profil sans
 * alertes) ne déclenche aucune lecture.
 *
 * Règle des finances (commit c4569258f, manuel 2 p. 78) : la clôture (dons
 * des messes à valider) n'est lue que pour un profil qui a les finances ; le
 * compte de reversement (montants) n'est lu que pour le curé.
 */
export function useAlertesParoisse(
  parishId: string | null | undefined,
  role: string,
  actif = true,
): AlertesParoisse {
  const pid = actif && voitAlertesParoisse(role) ? parishId ?? null : null;
  // Sources réservées à l'équipe pastorale par les services (versements,
  // demandes de sacrement, intentions de prière, visites) : curé seulement.
  const complet = role === "cure";
  // Demandes de documents de CATHO+ sans réponse : tout rôle qui les traite
  // (écran 42 : curé, vicaire, secrétaire ; décision PO du 31/10/2026).
  const voitDemandes = canAccessPage(role, 42);
  const finances = aAccesFinances(role);

  const lireCloture = finances ? pid : null;
  const c0 = useClotureSemaine(lireCloture, semaine(0));
  const c1 = useClotureSemaine(lireCloture, semaine(-1));
  const compte = useCompteReversement(pid, complet && finances);
  const intentions = useIntentionsDeMesseCompletes(pid);
  const demandes = useSacrementRequestsByParish(voitDemandes ? pid : null, { status: "SUBMITTED" });
  // Réponses de fidèles à une précision demandée, pas encore suivies d'un geste (01/11/2026).
  const enCoursDemandes = useSacrementRequestsByParish(voitDemandes ? pid : null, { status: "UNDER_REVIEW" });
  const prieres = useIntentionsARelire(complet ? pid : null);
  const visitesProg = useVisitesPastorales(complet ? pid : null, { statut: "PROGRAMMEE", size: 100 });
  const visitesReprog = useVisitesPastorales(complet ? pid : null, { statut: "A_REPROGRAMMER", size: 100 });

  const sources = [c0, c1, intentions, ...(voitDemandes ? [demandes, enCoursDemandes] : []), ...(complet ? [compte, prieres, visitesProg, visitesReprog] : [])];
  const enCours = sources.some((q) => q.isLoading);
  const enPanne = sources.some((q) => q.isError);

  const alertes = pid
    ? calculerAlertes({
        aujourdHui: new Date(),
        clotureCourante: c0.data,
        clotureEcoulee: c1.data,
        compte: complet && finances ? compte.data : null,
        intentions: intentions.data?.items ?? null,
        demandesSacrement: voitDemandes ? demandes.data ?? null : null,
        reponsesFideles: voitDemandes && enCoursDemandes.data
          ? enCoursDemandes.data.filter((d) => !!d.reponseFideleLe).length
          : null,
        prieresARelire: complet ? prieres.data?.total ?? null : null,
        visites:
          complet && (visitesProg.data || visitesReprog.data)
            ? [...(visitesProg.data?.items ?? []), ...(visitesReprog.data?.items ?? [])]
            : null,
      })
    : [];

  return {
    alertes,
    duJour: alertes.filter((a) => a.urgence === "jour").length,
    enCours,
    enPanne,
    complet,
  };
}
